import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { io } from "socket.io-client"; 

// ✅ Sound Config
const NOTIFICATION_SOUND_URL = "https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3"; 
// ✅ Socket URL
const SOCKET_URL = axiosInstance.defaults.baseURL || "http://localhost:5001"; 

const AdminChat = () => {
  const navigate = useNavigate();

  // --- Core States ---
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]); 
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // --- Features States ---
  const [searchQuery, setSearchQuery] = useState(""); 
  const [messageSearchQuery, setMessageSearchQuery] = useState(""); 
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showGlobalMenu, setShowGlobalMenu] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);
  
  // --- Firebase Status State ---
  const [firebaseStatus, setFirebaseStatus] = useState({ connected: false, loading: true });

  // --- Bulk Delete / Selection States ---
  const [isChatSelectionMode, setIsChatSelectionMode] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState([]);
  
  const [isMessageSelectionMode, setIsMessageSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);

  // --- Auto Delete & Modals ---
  const [showAutoDeleteModal, setShowAutoDeleteModal] = useState(false); 
  const [autoDeleteTarget, setAutoDeleteTarget] = useState("global"); 
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  
  // ✅ Image & Typing
  const [viewImage, setViewImage] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // --- Refs ---
  const socket = useRef(null);
  const scrollRef = useRef(); 
  const chatContainerRef = useRef();
  const fileInputRef = useRef();
  const menuRef = useRef();
  const globalMenuRef = useRef();
  const chatMenuRef = useRef();
  const audioRef = useRef(new Audio(NOTIFICATION_SOUND_URL)); 
  
  const selectedUserRef = useRef(null); 
  const isAtBottomRef = useRef(true); 

  const IMAGE_BASE_URL = axiosInstance.defaults.baseURL || "";

  // =============================================
  // ✅ FIREBASE STATUS CHECK
  // =============================================
  const checkFirebaseStatus = async () => {
    // 🛡️ FIX: Token ka naam 'accessToken' kiya
    if (!localStorage.getItem("accessToken")) return; 

    try {
      const response = await axiosInstance.get('/api/notification/firebase-status');
      setFirebaseStatus({ connected: response.data.connected, loading: false });
    } catch (err) {
      setFirebaseStatus({ connected: false, loading: false });
    }
  };

  // =============================================
  // 1. SOCKET.IO SETUP & INITIALIZATION
  // =============================================
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    // 🛡️ FIX: Token ka naam 'accessToken' kiya
    const token = localStorage.getItem("accessToken"); 
    
    if (!token) {
        console.log("User not logged in. Skipping chat initialization.");
        return; 
    }

    // A. Browser Notification Permission
    if (Notification.permission !== "granted") {
        Notification.requestPermission();
    }

    // B. Firebase Connection Check
    checkFirebaseStatus();

    // C. Connect Socket
    socket.current = io(SOCKET_URL, { transports: ['websocket'], withCredentials: true });

    socket.current.on("connect", () => {
        if (selectedUserRef.current) {
            socket.current.emit("join_chat", selectedUserRef.current.userId._id);
        }
    });

    // D. Receive Message Logic (With Sound & Pop-up)
    socket.current.on("receive_message", (newMsg) => {
        const currentUser = selectedUserRef.current;
        
        // Agar user ka message hai
        if (newMsg.sender !== 'admin') {
            audioRef.current.play().catch(() => {});

            if (Notification.permission === "granted") {
                new Notification(`New message from ${newMsg.senderName || 'User'}`, {
                    body: newMsg.messageType === 'image' ? "📷 Sent a photo" : newMsg.message,
                    icon: "/logo.png" 
                });
            }
        }

        if (currentUser && (String(newMsg.conversationId) === String(currentUser._id) || String(newMsg.sender) === String(currentUser.userId?._id) || String(newMsg.userId) === String(currentUser.userId?._id))) {
            setMessages(prev => {
                if (prev.some(m => m._id === newMsg._id)) return prev;
                return [...prev, newMsg];
            });
            if (isAtBottomRef.current || newMsg.sender === 'admin') {
                setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
            }
        }
        fetchUsers();
    });

    socket.current.on("update_inbox", fetchUsers);
    socket.current.on("typing", () => setIsTyping(true));
    socket.current.on("stop_typing", () => setIsTyping(false));
    socket.current.on("message_edited", (updatedMsg) => setMessages(prev => prev.map(m => m._id === updatedMsg._id ? updatedMsg : m)));
    socket.current.on("message_deleted", ({ messageId }) => setMessages(prev => prev.filter(m => m._id !== messageId)));

    fetchUsers();

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) setActiveMessageId(null);
        if (globalMenuRef.current && !globalMenuRef.current.contains(event.target)) setShowGlobalMenu(false);
        if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) setShowChatMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
        if(socket.current) socket.current.disconnect();
        window.removeEventListener("resize", handleResize);
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // =============================================
  // 2. SCROLL & UI LOGIC
  // =============================================
  const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      isAtBottomRef.current = (scrollHeight - scrollTop - clientHeight) < 150;
  };

  useEffect(() => {
    if (isAtBottomRef.current && chatContainerRef.current) {
        setTimeout(() => { if(chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight; }, 50);
    }
  }, [messages]);

  useEffect(() => {
      if (!searchQuery) {
          setFilteredUsers(users);
      } else {
          const lowerQ = searchQuery.toLowerCase();
          setFilteredUsers(users.filter(u => (u.userId?.userName?.toLowerCase().includes(lowerQ)) || (u.userId?.phone?.includes(searchQuery))));
      }
  }, [searchQuery, users]);

  // =============================================
  // 3. API CALLS
  // =============================================
  const fetchUsers = async () => {
    // 🛡️ FIX: Token ka naam 'accessToken' kiya
    if (!localStorage.getItem("accessToken")) return; 

    try {
      const response = await axiosInstance.get(`/api/chat/admin/inbox`);
      if (response.data?.success) {
          const data = response.data.data;
          setUsers(data.map(u => (selectedUserRef.current && u._id === selectedUserRef.current._id) ? { ...u, unreadCount: 0 } : u));
      }
    } catch (error) { console.error("Inbox Error:", error); }
  };

  const selectChat = async (chat) => {
      if (isChatSelectionMode) {
          toggleChatSelection(chat._id);
          return;
      }
      setSelectedUser(chat);
      setMessages([]);
      isAtBottomRef.current = true;
      setIsTyping(false);
      setIsMessageSelectionMode(false);
      setSelectedMessageIds([]);

      if (socket.current) socket.current.emit("join_chat", chat.userId._id);

      try {
        const response = await axiosInstance.get(`/api/chat/history/${chat.userId._id}`);
        if (response.data?.success) setMessages(response.data.data);
        await axiosInstance.put(`/api/chat/mark-read/${chat.userId._id}`);
        fetchUsers();
      } catch (error) { console.error(error); }
  };

  const saveAutoDeleteSettings = async (hours) => {
      try {
          await axiosInstance.post(`/api/chat/settings/auto-delete`, { userId: selectedUser?.userId?._id, deleteAfterHours: hours });
          alert(`✅ Auto-delete set to ${hours} hours`);
          setShowAutoDeleteModal(false);
      } catch (error) { alert("Failed to save"); }
  };

  // =============================================
  // 4. BULK DELETE HANDLERS
  // =============================================
  const toggleChatSelectionMode = () => {
      setIsChatSelectionMode(!isChatSelectionMode);
      setSelectedChatIds([]);
      setShowGlobalMenu(false);
  };

  const toggleChatSelection = (chatId) => {
      setSelectedChatIds(prev => prev.includes(chatId) ? prev.filter(id => id !== chatId) : [...prev, chatId]);
  };

  const deleteSelectedChats = async () => {
      if(!window.confirm(`Delete ${selectedChatIds.length} chats?`)) return;
      for (const chatId of selectedChatIds) {
          await axiosInstance.delete(`/api/chat/delete/${chatId}`, { data: { deleteType: 'all' } });
      }
      setIsChatSelectionMode(false);
      setSelectedChatIds([]);
      fetchUsers();
  };

  const toggleMessageSelectionMode = () => {
      setIsMessageSelectionMode(!isMessageSelectionMode);
      setSelectedMessageIds([]);
      setShowChatMenu(false);
  };

  const toggleMessageSelection = (msgId) => {
      setSelectedMessageIds(prev => prev.includes(msgId) ? prev.filter(id => id !== msgId) : [...prev, msgId]);
  };

  const deleteSelectedMessages = async () => {
      if(selectedMessageIds.length === 0) return;
      if(!window.confirm(`Delete ${selectedMessageIds.length} messages?`)) return;

      try {
          await axiosInstance.delete(`/api/chat/delete/bulk`, { data: { messageIds: selectedMessageIds } });
          setMessages(prev => prev.filter(m => !selectedMessageIds.includes(m._id)));
      } catch (e) {
          for (const id of selectedMessageIds) { 
             await axiosInstance.delete(`/api/chat/delete/${id}`, { data: { deleteType: 'everyone' } });
          }
      }
      setIsMessageSelectionMode(false);
      setSelectedMessageIds([]);
  };

  // =============================================
  // 5. MESSAGE HANDLERS
  // =============================================
  const handleInputChange = (e) => {
      setInputText(e.target.value);
      if (!socket.current || !selectedUser) return;
      socket.current.emit("typing", selectedUser.userId._id);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => socket.current.emit("stop_typing", selectedUser.userId._id), 2000);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText && !selectedFile) return;

    if (editingMessage) {
        try {
            const response = await axiosInstance.put(`/api/chat/edit/${editingMessage._id}`, { message: inputText });
            if (response.data?.success) { setEditingMessage(null); setInputText(""); }
        } catch (e) { alert("Edit Failed"); }
        return;
    }

    const formData = new FormData();
    formData.append("userId", selectedUser.userId._id);
    formData.append("sender", "admin");
    formData.append("message", inputText || "");
    if (selectedFile) { formData.append("image", selectedFile); formData.append("type", "image"); } else formData.append("type", "text");

    setInputText(""); setSelectedFile(null);
    if(fileInputRef.current) fileInputRef.current.value = "";
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (socket.current) socket.current.emit("stop_typing", selectedUser.userId._id);

    try {
      const response = await axiosInstance.post(`/api/chat/send`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (response.data?.success) {
        const sentMsg = response.data.data;
        setMessages(prev => { if (prev.some(m => m._id === sentMsg._id)) return prev; return [...prev, sentMsg]; });
        isAtBottomRef.current = true;
      }
    } catch (e) { console.error("Send Error", e); }
  };

  const handleDelete = async (msgId, deleteType) => {
      if (!window.confirm("Are you sure?")) return;
      try {
          if(deleteType === 'all') {
              await axiosInstance.delete(`/api/chat/delete/${selectedUser._id}`, { data: { deleteType: 'all' } });
              setMessages([]);
          } else {
              await axiosInstance.delete(`/api/chat/delete/${msgId}`, { data: { deleteType } });
          }
          setActiveMessageId(null);
      } catch (e) { console.error(e); }
  };

  const handleEditStart = (msg) => { setEditingMessage(msg); setInputText(msg.message || ""); setActiveMessageId(null); };
  const cancelEdit = () => { setEditingMessage(null); setInputText(""); };

  // =============================================
  // 6. HELPER FUNCTIONS & STYLES
  // =============================================
  const getFormattedDateLabel = (dateString) => {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return "Today";
      if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getGroupedMessages = () => {
      const groups = []; let lastDate = null;
      const filtered = messageSearchQuery ? messages.filter(m => m.message?.toLowerCase().includes(messageSearchQuery.toLowerCase())) : messages;
      filtered.forEach(msg => {
          const msgDate = new Date(msg.createdAt).toDateString();
          if (msgDate !== lastDate) {
              groups.push({ type: 'date_label', date: msg.createdAt, id: msgDate });
              lastDate = msgDate;
          }
          groups.push({ type: 'msg', data: msg });
      });
      return groups;
  };

  const styles = {
    wrapper: { display: "flex", height: isFullScreen ? "100vh" : "90vh", width: "100%", backgroundColor: "#fff", fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif", overflow: "hidden", position: isFullScreen ? "fixed" : "relative", top: 0, zIndex: 1000 },
    sidebar: { width: isMobile ? "100%" : "30%", minWidth: "320px", backgroundColor: "#fff", borderRight: "1px solid #d1d7db", display: (isMobile && selectedUser) ? "none" : "flex", flexDirection: "column" },
    sidebarHeader: { height: "70px", backgroundColor: isChatSelectionMode ? "#d9fdd3" : "#f0f2f5", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #d1d7db" },
    
    // Status Dot Style
    statusDot: (connected) => ({ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: connected ? '#22c55e' : '#ef4444', marginRight: '5px', boxShadow: connected ? '0 0 5px #22c55e' : 'none' }),
    
    searchContainer: { backgroundColor: "#fff", padding: "8px 12px", borderBottom: "1px solid #f0f2f5" },
    searchInputWrapper: { backgroundColor: "#f0f2f5", borderRadius: "8px", display: "flex", alignItems: "center", padding: "6px 12px", height: "35px" },
    searchInput: { border: "none", backgroundColor: "transparent", marginLeft: "15px", width: "100%", outline: "none", fontSize: "14px", color: "#3b4a54" },

    userList: { flex: 1, overflowY: "auto" },
    userItem: (active, isSelected) => ({ 
        display: "flex", padding: "12px 15px", cursor: "pointer", 
        backgroundColor: isSelected ? "#e3f2fd" : (active ? "#f0f2f5" : "#fff"), 
        borderBottom: "1px solid #f5f6f6", alignItems: "center", transition: "0.2s" 
    }),
    
    avatar: { width: "45px", height: "45px", borderRadius: "50%", backgroundColor: "#dfe5e7", display: "flex", justifyContent: "center", alignItems: "center", color: "#fff", fontSize: "20px", marginRight: "15px", position:'relative' },
    checkbox: { position: 'absolute', top: '-5px', left: '-5px', width: '18px', height: '18px', cursor:'pointer', accentColor: '#008069' },
    badge: { backgroundColor: "#25d366", color: "#fff", fontSize: "11px", padding: "3px 6px", borderRadius: "50%", fontWeight: "bold", minWidth: '18px', textAlign: 'center' },

    chatWindow: { flex: 1, display: isMobile && !selectedUser ? "none" : "flex", flexDirection: "column", backgroundColor: "#efeae2", backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')", backgroundRepeat: "repeat" },
    chatHeader: { height: "60px", backgroundColor: isMessageSelectionMode ? "#d9fdd3" : "#f0f2f5", padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #d1d7db", zIndex: 10 },
    
    messagesArea: { flex: 1, padding: "20px 5%", overflowY: "auto", display: "flex", flexDirection: "column" },
    dateLabel: { alignSelf: 'center', backgroundColor: '#fff', color: '#54656f', fontSize: '12px', padding: '5px 12px', borderRadius: '7px', marginBottom: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', textTransform: 'uppercase' },
    
    bubble: (sender) => ({ 
        maxWidth: "65%", padding: "6px 8px 6px 9px", borderRadius: "7.5px", 
        backgroundColor: sender === "admin" ? "#d9fdd3" : "#fff", 
        boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)", alignSelf: sender === "admin" ? "flex-end" : "flex-start", 
        marginBottom: "8px", position: "relative", color: "#111b21", fontSize: "14.2px", lineHeight: "19px", 
        display: "flex", flexDirection: "column", 
        borderTopRightRadius: sender === "admin" ? "0" : "7.5px", borderTopLeftRadius: sender === "admin" ? "7.5px" : "0" 
    }),
    
    chatImg: { width: "100%", maxWidth: "280px", maxHeight: "300px", objectFit: "cover", borderRadius: "6px", marginBottom: "5px", cursor: "pointer", border: "1px solid rgba(0,0,0,0.1)" },
    
    footer: { padding: "10px 16px", backgroundColor: "#f0f2f5", display: "flex", alignItems: "center", minHeight: "62px" },
    input: { flex: 1, padding: "12px 15px", borderRadius: "8px", border: "none", outline: "none", fontSize: "15px", backgroundColor: "#fff", margin: "0 10px", color: "#111b21" },
    icon: { color: "#54656f", fontSize: "22px", cursor: "pointer", padding: "8px" },
    
    menu: { position: "absolute", top: "45px", right: "20px", backgroundColor: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "5px", zIndex: 100, overflow: "hidden", minWidth: "180px" },
    menuItem: { padding: "12px 15px", cursor: "pointer", fontSize: "14px", color: "#3b4a54", borderBottom: "1px solid #f0f0f0", display: "block", width: "100%", textAlign: "left", background: "none", border: "none", hover: {backgroundColor: '#f5f6f6'} },
    
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 10000 },
    modalContent: { backgroundColor: "#fff", padding: "20px", borderRadius: "8px", width: "300px", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
    highlight: { backgroundColor: "#ffeb3b", color: "#000" },
    lightboxOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 100000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
    lightboxImg: { maxWidth: '90%', maxHeight: '90%', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' },
    closeLightbox: { position: 'absolute', top: '20px', right: '30px', color: '#fff', fontSize: '40px', cursor: 'pointer', fontWeight: 'bold' }
  };

  const MessageBubble = ({ msg }) => {
      const [hover, setHover] = useState(false);
      let displayUrl = msg.imageUrl?.startsWith("http") ? msg.imageUrl : `${IMAGE_BASE_URL}/${msg.imageUrl}`;

      const renderMessageText = () => {
          if (!msg.message) return null;
          const text = String(msg.message);
          if (!messageSearchQuery) return <span style={{color: '#111b21', wordBreak: 'break-word'}}>{text}</span>;
          const parts = text.split(new RegExp(`(${messageSearchQuery})`, 'gi'));
          return parts.map((part, i) => part.toLowerCase() === messageSearchQuery.toLowerCase() ? <span key={i} style={styles.highlight}>{part}</span> : part);
      };

      return (
          <div style={{display:'flex', justifyContent: msg.sender==='admin' ? 'flex-end' : 'flex-start'}} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
              <div style={styles.bubble(msg.sender)}>
                  {msg.messageType === 'image' && <img src={displayUrl} style={styles.chatImg} alt="img" onClick={() => setViewImage(displayUrl)} />}
                  {msg.message && <div style={{marginRight: '0px', marginBottom: '2px'}}>{renderMessageText()}</div>}
                  <div style={{display:'flex', alignSelf:'flex-end', alignItems:'center', marginTop:'-2px', minWidth: '60px', justifyContent: 'flex-end'}}>
                      <span style={{fontSize:'11px', color:'#667781', marginRight: '3px'}}>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {msg.sender === 'admin' && <i className="fa fa-check-double" style={{fontSize:'12px', color: msg.isRead ? '#53bdeb' : '#8696a0'}}></i>}
                  </div>
                  {isMessageSelectionMode && (
                      <div style={{position:'absolute', top:'10px', left: '-30px'}}>
                          <input type="checkbox" checked={selectedMessageIds.includes(msg._id)} onChange={() => toggleMessageSelection(msg._id)} style={{width:'18px', height:'18px', accentColor:'#008069'}}/>
                      </div>
                  )}
                  {!isMessageSelectionMode && hover && msg.sender === 'admin' && (
                      <div style={{position:'absolute', top:0, right:0, padding:'0 5px', cursor:'pointer'}} onClick={(e) => { e.stopPropagation(); setActiveMessageId(activeMessageId === msg._id ? null : msg._id); }}>
                          <i className="fa fa-angle-down" style={{color:'#8696a0'}}></i>
                      </div>
                  )}
                  {activeMessageId === msg._id && (
                      <div style={{...styles.menu, top:'20px', right:'5px', minWidth:'120px'}} ref={menuRef}>
                          <button style={styles.menuItem} onClick={() => { setIsMessageSelectionMode(true); toggleMessageSelection(msg._id); setActiveMessageId(null); }}>✅ Select</button>
                          <button style={styles.menuItem} onClick={() => handleEditStart(msg)}>Edit</button>
                          <button style={styles.menuItem} onClick={() => handleDelete(msg._id, 'me')}>Delete for Me</button>
                          <button style={{...styles.menuItem, color:'red'}} onClick={() => handleDelete(msg._id, 'everyone')}>Delete Everyone</button>
                      </div>
                  )}
              </div>
          </div>
      );
  };

  return (
    <div style={styles.wrapper}>
        <div style={styles.sidebar}>
            <div style={styles.sidebarHeader}>
                {isChatSelectionMode ? (
                    <div style={{display:'flex', width:'100%', justifyContent:'space-between', alignItems:'center'}}>
                        <div style={{fontWeight:'bold'}}>{selectedChatIds.length} Selected</div>
                        <div style={{display:'flex', gap:'15px'}}>
                            <i className="fa fa-trash" style={{color:'#d32f2f', cursor:'pointer', fontSize:'20px'}} onClick={deleteSelectedChats} title="Delete Selected"></i>
                            <i className="fa fa-times" style={{cursor:'pointer', fontSize:'20px', color: '#54656f'}} onClick={toggleChatSelectionMode}></i>
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{display:'flex', flexDirection:'column'}}>
                            <div style={{display:'flex', alignItems:'center'}}>
                                <div style={{...styles.avatar, background:'#dcf8c6', color:'#008069'}}><i className="fa fa-user-tie"></i></div>
                                <span style={{fontWeight:'bold', color:'#41525d'}}>Chats</span>
                            </div>
                            <div style={{display:'flex', alignItems:'center', marginTop:'5px', fontSize:'11px', color:'#667781'}}>
                                <div style={styles.statusDot(firebaseStatus.connected)} />
                                {firebaseStatus.loading ? "Checking Firebase..." : (firebaseStatus.connected ? "FCM Active" : "FCM Offline")}
                            </div>
                        </div>
                        <div style={{position:'relative'}}>
                            <i className="fa fa-sync-alt" style={{...styles.icon, fontSize:'16px'}} onClick={checkFirebaseStatus} title="Refresh Status"></i>
                            <i className="fa fa-ellipsis-v" style={styles.icon} onClick={() => setShowGlobalMenu(!showGlobalMenu)}></i>
                            {showGlobalMenu && (
                                <div style={styles.menu} ref={globalMenuRef}>
                                    <button style={styles.menuItem} onClick={toggleChatSelectionMode}>✔ Select Chats</button>
                                    <button style={styles.menuItem} onClick={() => { setAutoDeleteTarget("global"); setShowAutoDeleteModal(true); }}>⏳ Global Auto-Delete</button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div style={styles.searchContainer}>
                <div style={styles.searchInputWrapper}>
                    <i className="fa fa-search" style={{color:'#54656f', fontSize:'14px'}}></i>
                    <input placeholder="Search name or number" style={styles.searchInput} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
            </div>

            <div style={styles.userList}>
                {filteredUsers.map((chat) => (
                    <div key={chat._id} style={styles.userItem(selectedUser?._id === chat._id, selectedChatIds.includes(chat._id))} onClick={() => selectChat(chat)}>
                        <div style={styles.avatar}>
                            {chat.userId?.userName?.charAt(0).toUpperCase() || "U"}
                            {isChatSelectionMode && <input type="checkbox" checked={selectedChatIds.includes(chat._id)} readOnly style={styles.checkbox} />}
                        </div>
                        <div style={{flex:1, overflow:'hidden'}}>
                            <div style={{display:'flex', justifyContent:'space-between'}}>
                                <span style={{fontWeight:'500', color:'#111b21', fontSize:'16px'}}>{chat.userId?.userName}</span>
                                <span style={{fontSize:'12px', color: chat.unreadCount > 0 ? '#25d366' : '#667781'}}>{new Date(chat.updatedAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                            </div>
                            <div style={{display:'flex', justifyContent:'space-between', marginTop:'2px'}}>
                                <div style={{fontSize:'13px', color:'#667781', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', width:'80%'}}>
                                    {chat.lastMessageType === 'image' ? <span><i className="fa fa-camera"></i> Photo</span> : chat.lastMessage}
                                </div>
                                {chat.unreadCount > 0 && <span style={styles.badge}>{chat.unreadCount}</span>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        <div style={styles.chatWindow}>
            {selectedUser ? (
                <>
                    <div style={styles.chatHeader}>
                        <div style={{display:'flex', alignItems:'center'}}>
                            {isMobile && <i className="fa fa-arrow-left" style={{marginRight:'10px', color:'#54656f'}} onClick={() => setSelectedUser(null)}></i>}
                            <div style={styles.avatar}>{selectedUser.userId?.userName?.charAt(0).toUpperCase()}</div>
                            <div style={{cursor:'pointer'}} onClick={() => navigate(`/user-details/${selectedUser.userId?._id}`)}>
                                <div style={{fontWeight:'500', color:'#111b21'}}>{selectedUser.userId?.userName}</div>
                                <div style={{fontSize:'13px', color:'#667781'}}>
                                    {isTyping ? <span style={{color:'#25d366', fontWeight:'bold', fontStyle:'italic'}}>typing...</span> : (selectedUser.userId?.isOnline ? <span style={{color:'#25d366', fontWeight:'bold'}}>Online</span> : `Last seen recently`)}
                                </div>
                            </div>
                        </div>
                        <div style={{display:'flex', alignItems:'center', position:'relative'}}>
                            <i className={isFullScreen ? "fa fa-compress" : "fa fa-expand"} style={styles.icon} onClick={() => setIsFullScreen(!isFullScreen)} title="Full Screen"></i>
                            <i className="fa fa-search" style={styles.icon} onClick={() => setShowMessageSearch(!showMessageSearch)} title="Search Messages"></i>
                            <i className="fa fa-ellipsis-v" style={styles.icon} onClick={() => setShowChatMenu(!showChatMenu)}></i>
                            {showChatMenu && (
                                <div style={styles.menu} ref={chatMenuRef}>
                                    <button style={styles.menuItem} onClick={toggleMessageSelectionMode}>✔ Select Messages</button>
                                    <button style={styles.menuItem} onClick={() => { setAutoDeleteTarget("user"); setShowAutoDeleteModal(true); }}>⏳ Disappearing Messages</button>
                                    <button style={{...styles.menuItem, color:'red'}} onClick={() => handleDelete(selectedUser._id, 'all')}>🗑 Clear Chat</button>
                                </div>
                            )}
                        </div>
                    </div>
                    {showMessageSearch && (
                        <div style={{padding:'10px', backgroundColor:'#fff', borderBottom:'1px solid #ddd', display:'flex', alignItems:'center'}}>
                            <i className="fa fa-search" style={{color:'#999', marginRight:'10px'}}></i>
                            <input style={{border:'none', outline:'none', width:'100%', fontSize:'14px'}} placeholder="Search message..." value={messageSearchQuery} onChange={(e) => setMessageSearchQuery(e.target.value)} autoFocus />
                            <i className="fa fa-times" style={{cursor:'pointer', color:'#999'}} onClick={() => {setMessageSearchQuery(""); setShowMessageSearch(false);}}></i>
                        </div>
                    )}
                    <div style={styles.messagesArea} ref={chatContainerRef} onScroll={handleScroll}>
                        {isMessageSelectionMode && <div style={{padding:'10px', background:'#e3f2fd', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'8px', marginBottom:'10px'}}><span style={{fontWeight:'bold'}}>{selectedMessageIds.length} Selected</span><div style={{display:'flex', gap:'20px'}}><i className="fa fa-trash" style={{color:'red', cursor:'pointer', fontSize:'20px'}} onClick={deleteSelectedMessages}></i><i className="fa fa-times" style={{cursor:'pointer', fontSize:'20px'}} onClick={toggleMessageSelectionMode}></i></div></div>}
                        {getGroupedMessages().map((item, index) => (
                            item.type === 'date_label' ? <div key={item.id} style={{display:'flex', justifyContent:'center'}}><div style={styles.dateLabel}>{getFormattedDateLabel(item.date)}</div></div> : <MessageBubble key={index} msg={item.data} />
                        ))}
                        <div ref={scrollRef}></div>
                    </div>
                    {editingMessage && <div style={{backgroundColor:'#e9edef', padding:'10px 15px', borderLeft:'5px solid #008069', display:'flex', justifyContent:'space-between'}}><span style={{color:'#008069', fontWeight:'bold'}}>Editing Message...</span><i className="fa fa-times" style={{cursor:'pointer', color:'#54656f'}} onClick={cancelEdit}></i></div>}
                    {!isMessageSelectionMode && (
                        <form style={styles.footer} onSubmit={handleSendMessage}>
                            <i className="fa fa-smile" style={styles.icon}></i>
                            <input type="file" ref={fileInputRef} style={{display:'none'}} onChange={(e) => setSelectedFile(e.target.files[0])} />
                            <i className="fa fa-paperclip" style={styles.icon} onClick={() => fileInputRef.current.click()}></i>
                            {selectedFile && <div style={{fontSize:'12px', color:'#008069', marginRight:'5px'}}>{selectedFile.name}</div>}
                            <input style={styles.input} placeholder="Type a message" value={inputText} onChange={handleInputChange} />
                            {inputText || selectedFile ? <button type="submit" style={{background:'none', border:'none'}}><i className="fa fa-paper-plane" style={{color:'#008069', fontSize:'22px'}}></i></button> : <i className="fa fa-microphone" style={styles.icon}></i>}
                        </form>
                    )}
                </>
            ) : (
                <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', borderBottom:'6px solid #25d366'}}>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WA" style={{width:'80px', opacity:0.5, marginBottom:'20px'}} />
                    <h2 style={{color:'#41525d', fontWeight:'300'}}>WhatsApp Web Admin</h2>
                    <p style={{color:'#667781', marginTop:'10px'}}>Send and receive messages without keeping your phone online.</p>
                </div>
            )}
        </div>

        {showAutoDeleteModal && (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h3 style={{color:'#008069', marginBottom:'15px'}}>{autoDeleteTarget === 'user' ? '⏳ Disappearing Messages' : '⏳ Global Auto-Delete'}</h3>
                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        <button style={styles.menuItem} onClick={() => saveAutoDeleteSettings(24)}>After 24 Hours</button>
                        <button style={styles.menuItem} onClick={() => saveAutoDeleteSettings(168)}>After 7 Days</button>
                        <button style={{...styles.menuItem, color:'red'}} onClick={() => saveAutoDeleteSettings(0)}>❌ Off</button>
                    </div>
                    <button style={{marginTop:'20px', width:'100%', padding:'10px', backgroundColor:'#f0f2f5', border:'none', borderRadius:'5px'}} onClick={() => setShowAutoDeleteModal(false)}>Cancel</button>
                </div>
            </div>
        )}

        {viewImage && (
            <div style={styles.lightboxOverlay} onClick={() => setViewImage(null)}>
                <span style={styles.closeLightbox}>&times;</span>
                <img src={viewImage} style={styles.lightboxImg} alt="Full View" />
            </div>
        )}
    </div>
  );
};

export default AdminChat;